import { useRouter } from "next/router";
import { createContext, useCallback, useContext, useMemo } from "react";

export const DEFAULT_ITEMS_PER_PAGE = 24;

interface PaginationContextProps {
  currentPage: number;
  itemsPerPage: number;
  startPosition: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
}

const PaginationContext = createContext<PaginationContextProps>({
  currentPage: 0,
  itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
  startPosition: 0,
  onPageChange: () => {},
  onItemsPerPageChange: () => {},
});

export const PaginationProvider = (props) => {
  const { query, push } = useRouter();

  const value = useMemo(() => {
    const currentPage = +(query?.page ?? 1);
    const itemsPerPage = +(query?.itemsPerPage ?? DEFAULT_ITEMS_PER_PAGE);

    return {
      currentPage,
      itemsPerPage,
      startPosition: (+currentPage - 1) * itemsPerPage,
    };
  }, [query?.page, query?.itemsPerPage]);

  const onPageChange = useCallback(
    (page) => push({ query: { ...query, page } }),
    [query, push],
  );

  const onItemsPerPageChange = useCallback(
    (itemsPerPage) => push({ query: { ...query, page: 1, itemsPerPage } }),
    [query, push],
  );

  return (
    <PaginationContext.Provider
      value={{
        ...value,
        onPageChange,
        onItemsPerPageChange,
      }}
      {...props}
    />
  );
};

export const usePaginationContext = () => useContext(PaginationContext);
